import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/common/Button';
import { Film, Tv, Save, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { SEO } from '../components/SEO';
import { tmdbApi } from '../services/tmdb';
import blogService from '../services/blogService';
import { useTranslation } from '../hooks/useTranslation';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

interface MediaData {
  title: string;
  type: 'movie' | 'tv';
  tmdbId: number;
  year: number;
  image: string;
  poster: string;
  backdrop_path?: string;
  overview: string;
  rating: number;
  genres: { name: string }[];
  name: string;
  runtime?: number;
  seasons?: number;
  episodes?: number;
}

interface MediaSearchResult {
  id: number;
  title: string;
  releaseYear: number;
  type: 'movie' | 'tv';
  poster_path?: string;
}

interface MediaDetails {
  id: number;
  title: string;
  releaseYear: number;
  type: 'movie' | 'tv';
  poster_path?: string;
  backdrop_path?: string;
  overview: string;
  vote_average: number;
  genres: { id: number; name: string }[];
  runtime?: number;
  number_of_seasons?: number;
  number_of_episodes?: number;
}

interface MediaSelectorProps {
  onMediaSelect: (media: MediaDetails) => void;
}

interface BlogPostData {
  title: string;
  content: string;
  type: 'serie' | 'film';
  tmdbId: number;
  year: string;
  image: string;
  poster: string;
  overview: string;
  rating: number;
  tags: string[];
  authorId: string;
  runtime?: number;
  seasons?: number;
  episodes?: number;
}

interface CreatePostResponse {
  id: string;
}

const MediaSelector: React.FC<MediaSelectorProps> = ({ onMediaSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MediaSearchResult[]>([]);

  const handleSearch = async () => {
    if (!searchQuery) return;
    const { results } = await tmdbApi.searchMulti(searchQuery);
    
    const formattedResults = results.map((item: any) => ({
      id: item.id,
      title: item.title || item.name,
      releaseYear: item.release_date 
        ? new Date(item.release_date).getFullYear() 
        : item.first_air_date 
          ? new Date(item.first_air_date).getFullYear() 
          : 0,
      type: item.media_type === 'tv' ? 'tv' : 'movie',
      poster_path: item.poster_path
    }));
    
    setSearchResults(formattedResults);
  };

  const handleItemClick = (media: MediaSearchResult) => {
    const baseDetails: Partial<MediaDetails> = {
      id: media.id,
      title: media.title,
      releaseYear: media.releaseYear,
      type: media.type
    };
    onMediaSelect(baseDetails as MediaDetails);
  };

  return (
    <div>
      <input 
        type="text" 
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Rechercher un film/série"
      />
      <button onClick={handleSearch}>Rechercher</button>
      
      {searchResults.map((media) => (
        <div key={media.id} onClick={() => handleItemClick(media)}>
          {media.title} ({media.releaseYear})
        </div>
      ))}
    </div>
  );
};

const EditorContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-4);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-4);
`;

const Title = styled.h1`
  font-size: 2rem;
  color: var(--color-text-primary);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
`;

const ImagePreview = styled.div<{ $image: string }>`
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: var(--spacing-4);
  background: ${props => `url(${props.$image})`};
  background-size: cover;
  background-position: center;
  position: relative;
`;

const StyledImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: var(--border-radius);
`;

const Input = styled.input`
  width: 100%;
  padding: var(--spacing-3);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: var(--color-text-primary);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: var(--spacing-3);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: var(--color-text-primary);
  font-size: 1rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-3);
  margin-top: var(--spacing-4);
`;

const NewBlogPost = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const [selectedMedia, setSelectedMedia] = useState<MediaDetails | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (location.state?.mediaData) {
      setSelectedMedia(location.state.mediaData);
    }
  }, [location.state]);

  useEffect(() => {
    if (!user) {
      navigate('/login'); // Déplacer les navigations conditionnelles ici
    }
  }, [user, navigate]);

  if (!user || user.role !== 'admin') {
    navigate('/');
    return null;
  }

  const handleMediaSelect = async (media: MediaSearchResult) => {
    const details = await tmdbApi.getDetails(media.type as 'movie' | 'tv', media.id);
    
    const mediaDetails: MediaDetails = {
      id: details.id,
      title: details.title || details.name,
      releaseYear: details.release_date 
        ? new Date(details.release_date).getFullYear() 
        : details.first_air_date 
          ? new Date(details.first_air_date).getFullYear() 
          : 0,
      type: media.type,
      poster_path: details.poster_path,
      backdrop_path: details.backdrop_path,
      overview: details.overview,
      vote_average: details.vote_average,
      genres: details.genres || [],
      runtime: details.runtime,
      number_of_seasons: details.number_of_seasons,
      number_of_episodes: details.number_of_episodes
    };
    
    setSelectedMedia(mediaDetails);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!user) return;

    try {
      // Créer l'objet typé strictement
      const postData: BlogPostData = {
        title: title || selectedMedia?.title || 'Sans titre',
        content: content || '',
        type: selectedMedia?.type === 'tv' ? 'serie' : 'film',
        tmdbId: selectedMedia?.id || 0,
        year: selectedMedia?.releaseYear?.toString() || '',
        image: selectedMedia?.poster_path 
          ? `https://image.tmdb.org/t/p/w500${selectedMedia.poster_path}`
          : '',
        poster: selectedMedia?.poster_path 
          ? `https://image.tmdb.org/t/p/w500${selectedMedia.poster_path}`
          : '',
        overview: selectedMedia?.overview || '',
        rating: selectedMedia?.vote_average || 0,
        tags: selectedMedia?.genres?.map(g => g.name) || [],
        authorId: user.uid,
      };

      // Ajouter les champs optionnels seulement s'ils sont définis
      if (selectedMedia?.runtime !== undefined) postData.runtime = selectedMedia.runtime;
      if (selectedMedia?.number_of_seasons !== undefined) postData.seasons = selectedMedia.number_of_seasons;
      if (selectedMedia?.number_of_episodes !== undefined) postData.episodes = selectedMedia.number_of_episodes;

      console.log('Données du post:', postData);
      const result = await blogService.createPost(postData) as CreatePostResponse | string;
      const postId = typeof result === 'string' ? result : result.id;
      navigate(`/blog/${postId}`);
    } catch (error) {
      console.error('Erreur Firebase complète:', error);
      alert('Échec de la création. Vérifiez la console pour les détails.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <EditorContainer>
        <Header>
          <Title>{t.blog.newPost}</Title>
        </Header>

        <MediaSelector onMediaSelect={handleMediaSelect} />

        {selectedMedia && (
          <Form onSubmit={handleSubmit}>
            {selectedMedia.poster_path && (
              <ImagePreview $image={selectedMedia.poster_path}>
                <StyledImage 
                  src={`https://image.tmdb.org/t/p/w500${selectedMedia.poster_path}`} 
                  alt={`Affiche de ${selectedMedia?.title}`}
                />
              </ImagePreview>
            )}
            {selectedMedia.backdrop_path && (
              <ImagePreview $image={selectedMedia.backdrop_path}>
                <StyledImage 
                  src={`https://image.tmdb.org/t/p/original${selectedMedia.backdrop_path}`} 
                  alt={`Backdrop de ${selectedMedia?.title}`}
                />
              </ImagePreview>
            )}

            <Input
              name="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.blog.titlePlaceholder}
              required
            />

            <TextArea
              name="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t.blog.contentPlaceholder}
              required
            />

            <ButtonGroup>
              <Button
                type="submit"
                variant="primary"
                icon={<Save size={20} />}
                disabled={isSubmitting}
              >
                {t.common.save}
              </Button>
              <Button
                type="button"
                variant="secondary"
                icon={<X size={20} />}
                onClick={() => navigate(-1)}
              >
                {t.common.cancel}
              </Button>
            </ButtonGroup>
          </Form>
        )}

        {isSubmitting && <LoadingSpinner />}
      </EditorContainer>
    </Layout>
  );
};

export default NewBlogPost;
